/**
 * Dashboard Administrativo SAAS
 * Métricas, tenants, billing e controle geral
 */

import React, { useState, useEffect } from 'react';
import {
  Users, Building2, DollarSign, Activity, TrendingUp, AlertTriangle,
  Settings, BarChart3, PieChart, Calendar, ArrowLeft, RefreshCw
} from 'lucide-react';
import { TenantService } from '../services/tenantService';

const AdminDashboardPage = ({ onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    setLoading(true);
    try {
      // Simular dados admin (em produção, buscar do Firebase)
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Métricas gerais
      setMetrics({
        totalTenants: 47,
        activeTenants: 42,
        suspendedTenants: 3,
        canceledTenants: 2,
        
        totalUsers: 156,
        activeUsers: 134,
        
        mrr: 18450, // Monthly Recurring Revenue
        arr: 221400, // Annual Recurring Revenue
        churnRate: 2.8,
        ltv: 2840, // Lifetime Value
        cac: 180, // Customer Acquisition Cost
        
        aiUsage: {
          thisMonth: 45230,
          lastMonth: 38920,
          growth: 16.2
        },
        
        planDistribution: {
          basic: 28,
          professional: 15,
          enterprise: 4
        }
      });

      // Tenants recentes
      setTenants([
        {
          id: 'tenant_001',
          name: 'Escritório Silva & Associados',
          plan: 'professional',
          status: 'active',
          users: 3,
          mrr: 197,
          createdAt: '2024-01-15',
          lastActivity: '2024-01-20'
        },
        {
          id: 'tenant_002', 
          name: 'Advocacia Lima',
          plan: 'basic',
          status: 'active',
          users: 1,
          mrr: 97,
          createdAt: '2024-01-14',
          lastActivity: '2024-01-19'
        },
        {
          id: 'tenant_003',
          name: 'Jurídico Santos LTDA',
          plan: 'enterprise',
          status: 'trial',
          users: 8,
          mrr: 0,
          createdAt: '2024-01-18',
          lastActivity: '2024-01-20'
        }
      ]);

      // Atividade recente
      setRecentActivity([
        {
          type: 'signup',
          tenant: 'Escritório Novo',
          plan: 'basic',
          timestamp: '2024-01-20T10:30:00Z',
          icon: '👤'
        },
        {
          type: 'upgrade',
          tenant: 'Silva & Associados',
          plan: 'professional',
          timestamp: '2024-01-20T09:15:00Z',
          icon: '⬆️'
        },
        {
          type: 'payment',
          tenant: 'Advocacia Lima',
          amount: 'R$ 97',
          timestamp: '2024-01-20T08:00:00Z',
          icon: '💰'
        },
        {
          type: 'support',
          tenant: 'Jurídico Santos',
          issue: 'Integração PJe',
          timestamp: '2024-01-19T16:45:00Z',
          icon: '🎧'
        }
      ]);

    } catch (error) {
      console.error('❌ Erro ao carregar dados admin:', error);
    }
    setLoading(false);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      trial: 'bg-blue-100 text-blue-800',
      suspended: 'bg-yellow-100 text-yellow-800',
      canceled: 'bg-red-100 text-red-800'
    };
    
    const labels = {
      active: 'Ativo',
      trial: 'Trial',
      suspended: 'Suspenso',
      canceled: 'Cancelado'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const getPlanBadge = (plan) => {
    const styles = {
      basic: 'bg-gray-100 text-gray-800',
      professional: 'bg-blue-100 text-blue-800',
      enterprise: 'bg-purple-100 text-purple-800'
    };
    
    const labels = {
      basic: 'Básico',
      professional: 'Profissional',
      enterprise: 'Enterprise'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[plan]}`}>
        {labels[plan]}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando dashboard administrativo...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => onNavigate('dashboard')}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft size={16} />
                Voltar
              </button>
              <div className="flex items-center gap-3">
                <Settings className="w-6 h-6 text-blue-600" />
                <h1 className="text-xl font-bold text-gray-900">Admin Dashboard</h1>
              </div>
            </div>
            
            <button
              onClick={loadAdminData}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              <RefreshCw size={16} />
              Atualizar
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Métricas Principais */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">📊 Métricas Gerais</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Total Tenants */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 rounded-lg bg-blue-100">
                  <Building2 size={24} className="text-blue-600" />
                </div>
                <div className="text-xs text-green-600 font-medium">
                  +12% vs mês anterior
                </div>
              </div>
              <h3 className="text-sm font-medium text-gray-600 mb-1">Total Tenants</h3>
              <p className="text-2xl font-bold text-gray-900">{metrics.totalTenants}</p>
              <p className="text-xs text-gray-500 mt-1">
                {metrics.activeTenants} ativos • {metrics.suspendedTenants} suspensos
              </p>
            </div>

            {/* Total Users */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 rounded-lg bg-green-100">
                  <Users size={24} className="text-green-600" />
                </div>
                <div className="text-xs text-green-600 font-medium">
                  +8% vs mês anterior
                </div>
              </div>
              <h3 className="text-sm font-medium text-gray-600 mb-1">Total Usuários</h3>
              <p className="text-2xl font-bold text-gray-900">{metrics.totalUsers}</p>
              <p className="text-xs text-gray-500 mt-1">
                {metrics.activeUsers} ativos
              </p>
            </div>

            {/* MRR */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 rounded-lg bg-emerald-100">
                  <DollarSign size={24} className="text-emerald-600" />
                </div>
                <div className="text-xs text-green-600 font-medium">
                  +15% vs mês anterior
                </div>
              </div>
              <h3 className="text-sm font-medium text-gray-600 mb-1">MRR</h3>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(metrics.mrr)}</p>
              <p className="text-xs text-gray-500 mt-1">
                ARR: {formatCurrency(metrics.arr)}
              </p>
            </div>

            {/* Uso de IA */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 rounded-lg bg-purple-100">
                  <Activity size={24} className="text-purple-600" />
                </div>
                <div className="text-xs text-green-600 font-medium">
                  +{metrics.aiUsage.growth}% vs mês anterior
                </div>
              </div>
              <h3 className="text-sm font-medium text-gray-600 mb-1">Análises IA</h3>
              <p className="text-2xl font-bold text-gray-900">
                {metrics.aiUsage.thisMonth.toLocaleString('pt-BR')}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Este mês
              </p>
            </div>
          </div>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Distribuição de Planos */}
          <section>
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <PieChart className="w-5 h-5 text-blue-600" />
                Distribuição de Planos
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                    <span className="font-medium">Básico</span>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-gray-900">{metrics.planDistribution.basic}</div>
                    <div className="text-xs text-gray-500">
                      {Math.round(metrics.planDistribution.basic / metrics.totalTenants * 100)}%
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <span className="font-medium">Profissional</span>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-blue-900">{metrics.planDistribution.professional}</div>
                    <div className="text-xs text-blue-600">
                      {Math.round(metrics.planDistribution.professional / metrics.totalTenants * 100)}%
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                    <span className="font-medium">Enterprise</span>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-purple-900">{metrics.planDistribution.enterprise}</div>
                    <div className="text-xs text-purple-600">
                      {Math.round(metrics.planDistribution.enterprise / metrics.totalTenants * 100)}%
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Atividade Recente */}
          <section>
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-green-600" />
                Atividade Recente
              </h3>
              
              <div className="space-y-3">
                {recentActivity.map((activity, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                    <div className="text-lg">{activity.icon}</div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">
                        {activity.type === 'signup' && `Novo cadastro: ${activity.tenant}`}
                        {activity.type === 'upgrade' && `Upgrade para ${activity.plan}: ${activity.tenant}`}
                        {activity.type === 'payment' && `Pagamento ${activity.amount}: ${activity.tenant}`}
                        {activity.type === 'support' && `Suporte (${activity.issue}): ${activity.tenant}`}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(activity.timestamp).toLocaleString('pt-BR')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>

        {/* Lista de Tenants */}
        <section className="mt-8">
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                Tenants Recentes
              </h3>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tenant
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Plano
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Usuários
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      MRR
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Criado
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {tenants.map((tenant) => (
                    <tr key={tenant.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="font-medium text-gray-900">{tenant.name}</div>
                          <div className="text-xs text-gray-500">{tenant.id}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getPlanBadge(tenant.plan)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(tenant.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {tenant.users}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(tenant.mrr)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(tenant.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
